import React, { useEffect, useState, useCallback } from 'react'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { CTASection } from '../components/CTASection'
import { getBlogPosts, type BlogPost } from '../services/outline'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CalendarIcon, ClockIcon } from 'lucide-react'
import { slugify } from '../utils/slugify'
import { SeoHelmet } from '../components/SeoHelmet'
import { ROUTE_META } from '../data/routeMeta'

const PAGE_SIZE = 10

export function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const loadPosts = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getBlogPosts(PAGE_SIZE, page * PAGE_SIZE)
      setPosts(result.posts)
      setTotal(result.total)
    } catch {
      setError('Unable to load blog posts. Please try again later.')
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => {
    loadPosts()
  }, [loadPosts])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="min-h-screen w-full bg-white flex flex-col font-['Roboto']">
      <SeoHelmet {...ROUTE_META['/blog']} url="/blog" pageType="website" />
      <Header />

      <main className="flex-1">
        <section className="relative w-full bg-navy py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-transparent" />
          </div>

          <div className="max-w-5xl mx-auto relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white font-['Poppins']">
                Insights & <span className="text-primary">Field Notes</span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Thoughts on building production-ready AI, maintaining data
                privacy, and training teams for the future.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-8 text-center">
                {error}
              </div>
            ) : posts.length === 0 ? (
              <p className="text-center text-gray-500 py-20">
                No posts yet.
              </p>
            ) : (
              <>
                <div className="space-y-12">
                  {posts.map((post, index) => {
                    const date = new Date(post.publishedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                    return (
                      <motion.article
                        key={post.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4" />
                            <span>{date}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <ClockIcon className="w-4 h-4" />
                            <span>{post.readTime}</span>
                          </div>
                        </div>

                        <Link to={`/blog/${post.id}/${slugify(post.title)}`} className="block group">
                          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 font-['Poppins'] group-hover:text-primary transition-colors">
                            {post.title}
                          </h2>
                        </Link>

                        <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                          {post.excerpt}
                        </p>

                        <Link
                          to={`/blog/${post.id}/${slugify(post.title)}`}
                          className="inline-flex items-center font-medium text-primary hover:text-primary-dark transition-colors"
                        >
                          Read full article
                          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </motion.article>
                    )
                  })}
                </div>

                {totalPages > 1 && (
                  <nav className="flex items-center justify-center gap-4 mt-16" aria-label="Pagination">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                      Previous Page
                    </button>
                    <span className="text-sm text-gray-500 font-medium">
                      Page {page + 1} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= totalPages - 1}
                      className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                    >
                      Next Page
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </nav>
                )}
              </>
            )}
          </div>
        </section>

        <CTASection />
      </main>

      <Footer />
    </div>
  )
}